import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

import * as sinisterCardsActions from '../../../redux/actions/sinister-cards';
import { checkIfHotjarEnable } from '../../../utils/helpers';
import IconWarning from '../../../assets/ic_warning.svg';
import IconDropDoc from '../../../assets/ic_drop_doc.svg';
import IconTimesRed from '../../../assets/icons/ic_times_red.svg';
import IconDocument from '../../../assets/icons/ic_document.svg';

const MAX_TOTAL_FILES = 2;
const MAX_TOTAL_FILES_SIZE = 10; //MB
const MESSAGE_TIMER_DELAY = 5000;

function convertToMB(bytes) {
  return (bytes / 1024 / 1024).toFixed(2);
}

function readFile(file) {
  return window.URL.createObjectURL(file);
}

function dataFiles(file) {
  return {
    key: uuidv4(),
    type: file.type,
    name: file.name,
    size: file.size,
    content: readFile(file),
  };
}

const DropFileInput = ({
  title,
  typeFile,
  typeFileId,
  currentFiles,
  maxFiles = MAX_TOTAL_FILES,
  onLoadFile,
  onDeleteFile
}) => {
  const dispatch = useDispatch();
  const [fileError, setFileError] = useState(false);
  const [files, setFiles] = useState(
    currentFiles?.files?.length > 0 ? currentFiles.files : []
  );
  const [totalFilesSize, setTotalFilesSize] = useState(0);

  useEffect(() => {
    const totalFileSize = files.reduce((sum, currFile) => {
      return sum + currFile.size;
    }, 0);
    const totalFileSizeInMB = isNaN(totalFileSize)
      ? 0
      : convertToMB(totalFileSize);
    setTotalFilesSize(totalFileSizeInMB);
  }, [files, setFiles]);

  useEffect(() => {
    let hideMessageTimer;
    if (fileError) {
      hideMessageTimer = setTimeout(() => {
        setFileError('');
      }, MESSAGE_TIMER_DELAY);
    }

    return () => {
      clearTimeout(hideMessageTimer);
    };
  }, [fileError]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const totalFileSize = acceptedFiles.reduce((sum, currFile) => {
        return sum + currFile.size;
      }, 0);
      const totalFileSizeInMB = isNaN(totalFileSize)
        ? 0
        : convertToMB(totalFileSize);
      if (totalFileSizeInMB > MAX_TOTAL_FILES_SIZE) {
        setFileError('El tamaño máximo permitido es de 10MB');
        if (checkIfHotjarEnable()) {
           // LAST
          window.digitalData.push({
            log: {
              message: 'Recuerda que el tamaño máximo de un archivo es de 10MB',
            },
            event: 'trackLog',
          });
        }
        return;
      }

      if (files.length > maxFiles) {
        setFileError(
          `Puedes adjuntar como máximo ${maxFiles} archivo(s) por cada documento solicitado.`
        );
        return;
      }

      const newFiles = [...files];
      let isFileError = false;
      for (let acceptedFile of acceptedFiles) {
        const file = dataFiles(acceptedFile);
        const isNotUnique = newFiles.some((item) => item.name === file.name);
        newFiles.push(file);

        if (newFiles.length > maxFiles) {
          setFileError(
            `Puedes adjuntar como máximo ${maxFiles} archivo(s) por cada documento solicitado.`
          );
          isFileError = true;
          break;
        } else if (isNotUnique) {
          if (checkIfHotjarEnable()) {
            // LAST
            window.digitalData.push({
              "log": {
                "message": "Parece que ya subiste este archivo"
              },
              "event": "trackLog"
            });
          }
          setFileError('*Parece que ya subiste este archivo.');
          isFileError = true;
          break;
        }
      }

      if (!isFileError) {
        onLoadFile && onLoadFile(title);
        setFiles(newFiles);
        dispatch(
          sinisterCardsActions.storeNewFiles({
            title: title,
            id: typeFileId,
            typeFile: typeFile,
            files: newFiles,
          })
        );
      }
    },
    [files]
  );

  const onDropRejected = useCallback((rejectedFiles) => {
    const errorType = rejectedFiles[0].errors[0].code;
    console.log(errorType);

    // check if file type is not allowed
    // check if number of files is more than the max
    if (errorType === 'file-invalid-type') {
      setFileError(
        'Recuerda que los archivos deben estar en formato JPG, PNG o PDF.'
      );

      if (checkIfHotjarEnable()) {
         // LAST
        window.digitalData.push({
          log: {
            message: 'Recuerda que los archivos deben estar en formato JPG, PNG o PDF',
          },
          event: 'trackLog',
        });
      }
    } else if (errorType === 'too-many-files') {
      setFileError(
        `Puedes adjuntar como máximo ${maxFiles} archivo(s) por cada documento solicitado.`
      );
    }
  });
  const typeFileValidator = (file) => {
    const fileTypesAllowed = ['application/pdf','image/png', 'image/jpg', 'image/jpeg']
    console.log('typeFileValidator file', file);
    if (!fileTypesAllowed.includes(file.type) ) {
      return {
        code: "file-invalid-type",
        message: ''
      };
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/jpg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    maxFiles,
    multiple: true,
    validator: typeFileValidator
  });

  const deleteFile = (key) => {
    const filteredFiles = files.filter((file) => file.key !== key);

    setFiles(filteredFiles);

    dispatch(
      sinisterCardsActions.storeNewFiles({
        title: title,
        id: typeFileId,
        typeFile,
        files: filteredFiles,
      })
    );
    onDeleteFile && onDeleteFile(title);
  };

  const fileCapacityLeft = (max, current) => {
    return (Math.round((max - current) * 100) / 100).toFixed(2);
  };

  return (
    <>
      {files.length < maxFiles && (
        <div className="dropInput" {...getRootProps()}>
          <input {...getInputProps()} />
          <div className={`dropInput__cont ${isDragActive ? 'active' : ''}`}>
            <img
              className="dropInput__img "
              src={IconDropDoc}
              alt="arrastra tu archivo aquí"
            />
            <div className="dropInput__info">
              <h4>
                Arrastre el archivo a esta área o{' '}
                <span className="purple">haz click aquí</span>
              </h4>
              <p>
                {totalFilesSize > 0
                  ? `Queda ${String(
                      fileCapacityLeft(10, totalFilesSize)
                    )} MB - Formatos JPG, PNG, PDF`
                  : 'Máximo 10 MB - Formatos JPG, PNG, PDF'}
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="dropInput__error">
        {fileError && (
          <div className="attachFileAccount__message">
            <img src={IconTimesRed} alt="error en el archivo adjunto" />
            <p>{fileError}</p>
          </div>
        )}
      </div>
      <div className="dropInput__filesInfo">
        {files.length === 0 ? (
          <div className="warningMsg">
            <img src={IconWarning} alt="No hay archivos cargados" />
            <p>Aún no has cargado un archivo</p>
          </div>
        ) : (
          `Máximo ${maxFiles} archivo(s). ${files.length || '0'}/${maxFiles}`
        )}
      </div>
      <div className="dropInput__files">
        {files.map((file, key) => (
          <div className="docInfoCard" key={key}>
            <div className="docInfoCard__name">
              <img src={IconDocument} alt="documento adjunto" />
              <p>
                {`${convertToMB(file.size)} MB`} - <b>{file.name}</b>
              </p>
            </div>
            {file.pending ? (
              <div className="loader">
                <div className="loader-style-1" />
              </div>
            ) : (
              <div
                className="docInfoCard__delete"
                onClick={() => deleteFile(file.key)}
              >
                Eliminar
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default DropFileInput;
